"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Heart,
  Sparkles,
  Users,
  MessageSquare,
  Shield,
  TrendingUp,
  Target,
  Award,
  ArrowRight,
  Crown,
  Activity,
  Briefcase,
  Globe,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { Button } from "@/components/ui/button";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import {
  CheckCircle2,
  CheckSquare,
  ListChecks,
  ShieldCheck,
  Repeat,
  Search,
  Settings,
} from "lucide-react";

const ADVANTAGES = [
  {
    icon: MessageSquare,
    title: "Communication",
    body: "Problems surface earlier because team members are willing to say the uncomfortable thing in the room before it becomes a catastrophe in the boardroom. Information flows freely, problems are surfaced without fear, and the early warning system is built into the team's DNA.",
    accent: "red",
    metric: "3x",
    metricLabel: "Faster problem identification",
  },
  {
    icon: Shield,
    title: "Risk Discipline",
    body: "Protocols and systems that distinguish operational excellence from operational luck are followed meticulously. Risk is managed, not courted. The 99.9999% uptime standard — maximum 31.5 seconds of downtime per year — is the product of this discipline.",
    accent: "purple",
    metric: "99.9999%",
    metricLabel: "Uptime",
  },
  {
    icon: Users,
    title: "Collaboration Over Competition",
    body: "Internal energy goes outward, toward the mission, instead of inward, toward each other. The teams compete against the problem, not against each other — freeing up to forty percent of productive capacity typically lost to internal politics.",
    accent: "emerald",
    metric: "+40%",
    metricLabel: "Coordination efficiency",
  },
  {
    icon: TrendingUp,
    title: "Retention",
    body: "Institutional knowledge is preserved because the environment is one where people can stay and thrive. Every departure is a loss; high retention is not HR, it is operational. The 70% Operating Model produces teams that stay.",
    accent: "cyan",
    metric: "5+ yr",
    metricLabel: "Average tenure",
  },
  {
    icon: Target,
    title: "Ego Management",
    body: "Ego interferes less with execution. The operational systems function as designed, without distortions. In teams where ego is managed, execution is cleaner, faster, and more reliable — essential in compartmentalized sovereign operations.",
    accent: "amber-gold",
    metric: "0",
    metricLabel: "Security incidents",
  },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20" },
};

const HIRING_TRAITS = [
  { num: "01", title: "Rigor", body: "The ability to follow protocols and maintain standards under pressure. Non-negotiable in sovereign-scale operations." },
  { num: "02", title: "Communication", body: "The willingness to surface problems early and clearly, before they escalate into crises." },
  { num: "03", title: "Risk Discipline", body: "The capacity to manage risk carefully rather than recklessly, to follow the systems that distinguish excellence from luck." },
  { num: "04", title: "Retention", body: "The likelihood of staying with the organization long enough to build institutional knowledge." },
  { num: "05", title: "Collaboration", body: "The ability to make the people around them better, not just to perform individually." },
];

const EVIDENCE = [
  { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "text-cyan" },
  { metric: "83.3", suffix: "%", label: "Success", sub: "15 of 18 engagements", accent: "text-emerald" },
  { metric: "900", suffix: "M+", label: "Voters", sub: "Continental reach", accent: "text-amber-gold" },
  { metric: "70", suffix: "%", label: "Women", sub: "By selection, results", accent: "text-red" },
  { metric: "0", suffix: "", label: "Breaches", sub: "15+ years clean", accent: "text-emerald" },
  { metric: "15", suffix: "+", label: "Languages", sub: "Operational coverage", accent: "text-purple" },
];

export default function SeventyPercentProjectPage() {
  return (
    <>
      <PageHero
        icon={Heart}
        overline="The 70% Project"
        overlineClassName="text-red"
        title={
          <>
            Building Teams That <span className="text-gradient-sovereign">Outperform.</span>
          </>
        }
        subtitle="The operating case for the talent the world overlooks. Not a moral argument — an operational finding, backed by a decade of sovereign-scale evidence across eighteen countries."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "The 70% Project" }]}
        badge="Decade of verified evidence"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-red border-red/20 mb-5">
              <Sparkles className="size-3.5 text-red" />
              <span className="text-overline text-red">The Premise</span>
            </div>
            <h2 className="text-display-md mb-5">The world&apos;s most reliable talent pool is the one the world is systematically overlooking.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The world has a talent allocation problem — and it does not know it. Organizations select on the wrong signals. Confidence is mistaken for competence. Volume is mistaken for capability. Self-promotion is mistaken for results.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The most reliable operational performers — the people who communicate clearly, manage risk carefully, follow protocols rigorously, stay with the organization long enough to build institutional knowledge, and make the people around them better — are being passed over. The cost is measured in projects that fail, teams that fragment, and competitive advantage that evaporates with every turnover cycle.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => (
              <motion.div
                key={e.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass-card rounded-2xl p-5 bg-gradient-to-br from-red/10 to-transparent border border-red/20 hover:scale-[1.01] transition-all"
              >
                <div className={`text-display-sm font-bold ${e.accent} leading-none mb-2`}>
                  {e.metric}{e.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                <div className="text-caption text-muted-foreground">{e.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-purple border-purple/20 mb-4">
            <Target className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Method</span>
          </div>
          <h2 className="text-display-md mb-4">The five hiring traits.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Optimize for these five traits — and the talent pool that emerges is consistently majority-women. Not by design. By selection.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {HIRING_TRAITS.map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-purple/10 to-transparent border border-purple/20"
            >
              <div className="text-display-sm font-bold text-purple/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-purple">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-coral border-coral/20 mb-4">
            <Search className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Selection Architecture</span>
          </div>
        </div>
        <ArchitectureDiagramBlock
          eyebrow="The Selection Criteria"
          title="What the model actually selects for."
          description="Five operational signals — and the assessment domains that verify them. The selection criteria that produce the 70% Operating Model, drawn from a decade of sovereign-scale hiring."
          center={{
            label: "70% Project",
            body: "Trait-based selection at sovereign scale. The architectural center of every hiring decision.",
            icon: <Target className="size-5" />,
          }}
          satellites={[
            {
              label: "Rigor",
              body: "Work simulations, protocol exercises, and structured interviews that reveal whether the candidate follows systems under pressure.",
              icon: <ListChecks className="size-4" />,
              accent: "coral",
            },
            {
              label: "Communication",
              body: "Behavioral assessment of surfacing problems early, asking precise questions, and disagreeing without dominating.",
              icon: <MessageSquare className="size-4" />,
              accent: "coral",
            },
            {
              label: "Risk Discipline",
              body: "Scenario-based evaluation of how candidates manage uncertainty — methodical versus reckless, calibrated versus cutting corners.",
              icon: <ShieldCheck className="size-4" />,
              accent: "coral",
            },
            {
              label: "Retention Signal",
              body: "Tenure analysis, reference checks on prior team continuity, and pattern recognition for institutional knowledge accumulation.",
              icon: <Repeat className="size-4" />,
              accent: "coral",
            },
            {
              label: "Collaboration",
              body: "Group exercises and team simulations that surface how candidates make the people around them better, not just how they perform individually.",
              icon: <Users className="size-4" />,
              accent: "coral",
            },
            {
              label: "Outcome Orientation",
              body: "Behavioral interviewing focused on whether candidates define success by outcomes delivered — not by effort expended or hours logged.",
              icon: <CheckCircle2 className="size-4" />,
              accent: "coral",
            },
          ]}
          connections={[
            { from: -1, to: 0 },
            { from: -1, to: 1 },
            { from: -1, to: 2 },
            { from: -1, to: 3 },
            { from: -1, to: 4 },
            { from: -1, to: 5 },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-emerald border-emerald/20 mb-4">
              <Award className="size-3.5 text-emerald" />
              <span className="text-overline text-emerald">Why This Is Not a Diversity Program</span>
            </div>
            <h2 className="text-display-md mb-4">The argument from operations, not morality.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                icon: TrendingUp,
                title: "Measurable, Verifiable Results",
                body: "A diversity target can be met by hiring to a number. The 70% Operating Model cannot be gamed — the metric is not composition, but performance. Teams that do not communicate, manage risk, follow protocols, and retain institutional knowledge are not successes regardless of composition.",
              },
              {
                icon: ShieldCheck,
                title: "Resilient to Political Shifts",
                body: "Morality-based programs rise and fall with political winds. Operational findings do not — they are grounded in data, not ideology. An organization that adopts the 70% Operating Model does so because it produces better results, not because a policy mandates it.",
              },
              {
                icon: CheckSquare,
                title: "Self-Reinforcing Evidence",
                body: "When teams outperform, the evidence accumulates. When outcomes improve, the case strengthens. The 70% Operating Model does not require advocacy — it requires documentation. The results speak for themselves.",
              },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="glass-card rounded-2xl p-5 bg-gradient-to-br from-emerald/10 to-transparent border border-emerald/20"
              >
                <b.icon className="size-6 text-emerald mb-3" />
                <h3 className="text-heading-md mb-2">{b.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-amber-gold border-amber-gold/20 mb-4">
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Operating Advantages</span>
          </div>
          <h2 className="text-display-md mb-4">Why majority-female teams outperform.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five observed, documented, and verified advantages — across a decade of sovereign-scale operations.
          </p>
        </div>
        <div className="space-y-4">
          {ADVANTAGES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
                  <div className={`size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${ac.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <a.icon className="size-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-overline ${ac.text}`}>Advantage 0{i + 1}</span>
                      <span className="text-muted-foreground opacity-30">·</span>
                      <span className="text-caption text-muted-foreground">{a.metricLabel}</span>
                    </div>
                    <h3 className={`text-display-sm mb-2 ${ac.text}`}>{a.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-display-md font-bold ${ac.text} leading-none`}>{a.metric}</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-cyan border-cyan/20 mb-4">
            <Crown className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Naming</span>
          </div>
          <h2 className="text-display-md mb-5">Why seventy percent.</h2>
          <p className="text-body-lg text-muted-foreground leading-relaxed mb-5">
            Seventy percent is not a round number chosen for marketing. It is the observed average across multiple teams, multiple countries, and multiple years.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            In some teams, the figure reaches eighty percent. In others, it settles around sixty-five. The consistency — that the number hovers around seventy percent across vastly different contexts — is what makes it an operational finding rather than an anomaly.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The name reflects the evidence — not the aspiration.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-3 gap-6">
          {[
            { icon: Activity, title: "The Duration", body: "This pattern has been consistent across a decade of operations — not a single quarter, not a single team, but a sustained operational finding. The data set is mature, verified, and reproducible." },
            { icon: Globe, title: "The Scale", body: "The evidence encompasses operations across eighteen countries on three continents. It spans engagements with governments, royal families, political leaders, Fortune 500 corporations, and defense agencies." },
            { icon: Briefcase, title: "The Outcomes", body: "An 83.3% campaign success rate. 900 million voters influenced. 100% client satisfaction. 99.9999% uptime. Zero security incidents across fifteen-plus years. The 70% Operating Model is not a small-sample finding." },
          ].map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card rounded-2xl p-6 bg-gradient-to-br from-amber-gold/10 to-transparent border border-amber-gold/20"
            >
              <b.icon className="size-8 text-amber-gold mb-4" />
              <h3 className="text-heading-md mb-3">{b.title}</h3>
              <p className="text-body-sm text-muted-foreground leading-relaxed">{b.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-denim border-denim/20 mb-4">
              <TrendingUp className="size-3.5 text-denim" />
              <span className="text-overline text-denim">The Economic Case</span>
            </div>
            <h2 className="text-display-md mb-4">The most reliable competitive advantage available.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-4">
              The world is sitting on the most reliable competitive advantage available — and it is ignoring it. The talent that produces the most reliable operational outcomes is the talent that the current system overlooks: women who communicate clearly, manage risk carefully, follow protocols rigorously, stay with organizations long enough to build institutional knowledge, and make the people around them better.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              This is a talent arbitrage. The market is mispricing this talent. Organizations that recognize the mispricing — and that redesign their selection processes to capture this talent — gain a structural advantage that compounds over time.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { num: "01", title: "Capture the Mispriced Pool", body: "Talent that communicates, collaborates, manages risk, and stays is available at market rates because the system does not select for these traits. Organizations that recognize this gain access to talent that outperforms without paying the premium that conventional labels command." },
              { num: "02", title: "Build a Data Moat", body: "The compounding effect is the long-term advantage: organizations that adopt the model early build a data advantage that late adopters cannot replicate. The first hundred teams produce data that sharpens the selection process. By the thousandth team, the data set is a competitive moat." },
              { num: "03", title: "Close the Gap First", body: "The arbitrage is not permanent. As more organizations recognize the mispricing, the gap will close. The organizations that move first capture the advantage while it is still available. The 70% Project is the early warning system for this shift." },
              { num: "04", title: "Outperform on Every Metric", body: "Organizations that close this gap do not merely hire better. They operate better. They outperform competitors who are still selecting on the wrong signals. They retain institutional knowledge while competitors hemorrhage it. They build cultures of communication and collaboration while competitors build cultures of competition and ego." },
            ].map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass-card rounded-2xl p-5 bg-gradient-to-br from-denim/10 to-transparent border border-denim/20"
              >
                <div className="flex items-start gap-4">
                  <div className="text-display-sm font-bold text-denim/40 leading-none shrink-0">{s.num}</div>
                  <div>
                    <h3 className="text-heading-md mb-2 text-denim">{s.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-cyan border-cyan/20 mb-4">
            <Settings className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Implementation Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Five phases to adopt the 70% Operating Model.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The path from current practice to the 70% Operating Model — assessment, redesign, pilot, scale, embed. The clinical method applied to team building.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { phase: "01", title: "Assessment", body: "Evaluate current hiring practices. Identify the signals being optimized for. Measure current team performance. The baseline against which the model will be measured.", accent: "cyan" },
            { phase: "02", title: "Redesign", body: "Redesign the selection process to optimize for the five traits. Develop structured interview protocols, design work simulations, establish outcome measurement criteria.", accent: "cyan" },
            { phase: "03", title: "Pilot", body: "Implement in one team or department. Measure outcomes against baseline. Generate the internal evidence that supports scaling — proof in this organization, with this culture.", accent: "cyan" },
            { phase: "04", title: "Scale", body: "Expand the operating model across the organization. Measure, iterate, improve. The compounding effect begins — the data set grows, selection sharpens, outcomes improve.", accent: "cyan" },
            { phase: "05", title: "Embed", body: "Make the 70% Operating Model the default hiring practice. Measure outcomes continuously. The model becomes institutional — part of the operating DNA, not a program.", accent: "cyan" },
          ].map((s, i) => (
            <motion.div
              key={s.phase}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="glass-card rounded-2xl p-5 bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20"
            >
              <div className="text-display-sm font-bold text-cyan/40 leading-none mb-3">{s.phase}</div>
              <h3 className="text-heading-md mb-2 text-cyan">{s.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Get involved.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The 70% Project is a decade of evidence, a replicable method, and a transformative talent strategy. Connect to learn more, contribute, or bring the 70% Operating Model to your organization.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button asChild size="lg" className="h-12 px-6 bg-gradient-sovereign shadow-lg">
                <Link href="/contact/">
                  Connect on the 70% Project
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-6 glass">
                <Link href="/insights/seventy-percent-women/">
                  Read the Thesis Essay
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Heart, accent: "red" },
          { title: "The Operator's Notebook", description: "Biweekly notes on how excellent things run.", href: "/the-operators-notebook/", icon: Sparkles, accent: "amber-gold" },
          { title: "Insights", description: "The full body of thought leadership. The clinical method applied to organizations.", href: "/insights/", icon: Activity, accent: "purple" },
          { title: "Speaking Topics", description: "Six signature keynotes, including The 70% Project.", href: "/speaking/the-70-percent-project/", icon: MessageSquare, accent: "cyan" },
        ]}
      />
    </>
  );
}
